<template>
  <div class="page-wrapper">
    <HeaderComponent />

    <div class="page-content">
      <AppBreadcrumb :items="[{ label: $t('result.breadcrumb') }]" />

      <a-alert
        v-if="errorMessage"
        type="error"
        show-icon
        :message="errorMessage"
        style="margin-bottom: 16px"
      />

      <template v-if="loading">
        <a-card class="custom-card result-card">
          <a-skeleton active />
        </a-card>
      </template>

      <template v-else-if="result">
        <div class="result-wrap">
          <a-card class="custom-card result-card">
            <div class="result-top">
              <div class="result-icon">
                {{ result.failedByMistakes ? '⚠️' : '✅' }}
              </div>

              <div class="result-title-wrapper">
                <h1 class="result-title">
                  {{ result.failedByMistakes ? $t('result.testFinishedEarly') : $t('result.testFinished') }}
                </h1>
                <p class="result-subtitle">
                  {{ result.title }}
                </p>
              </div>
            </div>

            <!-- Stats grid - fully responsive -->
            <div class="stats-grid">
              <div class="stat-box">
                <div class="stat-label">{{ $t('result.totalQuestions') }}</div>
                <div class="stat-value">{{ result.totalQuestions }}</div>
              </div>

              <div class="stat-box success-box">
                <div class="stat-label">To‘g‘ri</div>
                <div class="stat-value">{{ result.correctCount }}</div>
              </div>

              <div class="stat-box danger-box">
                <div class="stat-label">{{ $t('result.wrong') }}</div>
                <div class="stat-value">{{ result.wrongCount }}</div>
              </div>

              <div class="stat-box primary-box">
                <div class="stat-label">{{ $t('result.percent') }}</div>
                <div class="stat-value">{{ result.percent }}%</div>
              </div>
            </div>

            <div class="result-progress">
              <div class="result-progress-head">
                <span>{{ $t('result.resultIndicator') }}</span>
                <span>{{ result.percent }}%</span>
              </div>
              <a-progress :percent="Number(result.percent)" :show-info="false" />
            </div>

            <div v-if="result.unansweredCount > 0" class="info-note">
              {{ $t('result.unanswered', { count: result.unansweredCount }) }}
            </div>

            <div v-if="result.failedByMistakes" class="warning-note">
              {{ $t('result.failedByMistakes') }}
            </div>

            <div class="result-actions">
              <a-button type="primary" size="large" block @click="restartTest">
                {{ $t('result.restart') }}
              </a-button>

              <a-button
                v-if="showBackToCategory"
                size="large"
                block
                @click="goBackToCategory"
              >
                {{ $t('result.backToCategory') }}
              </a-button>

              <a-button v-else size="large" block @click="goHome">
                Bosh sahifa
              </a-button>
            </div>
          </a-card>
        </div>
      </template>

      <template v-else>
        <a-empty :description="$t('result.notFound')">
          <a-button type="primary" @click="goHome">{{ $t('result.homePage') }}</a-button>
        </a-empty>
      </template>
    </div>
  </div>
</template>

<script>
import api, { MEDIA_BASE_URL } from '@/api/axios'
import HeaderComponent from '../components/HeaderComponent.vue'
import AppBreadcrumb from '../components/AppBreadcrumb.vue'

export default {
  name: 'ResultView',

  components: {
    HeaderComponent,
    AppBreadcrumb
  },

  data() {
    return {
      loading: true,
      errorMessage: '',
      result: null
    }
  },

  computed: {
    resultId() {
      return Number(this.$route.query.result_id) || null
    },

    showBackToCategory() {
      return this.result?.backRoute?.startsWith('/tickets/')
    }
  },

  async created() {
    if (!this.resultId) {
      this.errorMessage = this.$t('result.idNotFound')
      this.loading = false
      return
    }

    await this.fetchResult()
  },

  methods: {
    getAuthHeaders() {
      const token = localStorage.getItem('accessToken') || ''
      return {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {})
      }
    },

    async fetchResult() {
      this.loading = true
      this.errorMessage = ''
      this.result = null

      try {
        const response = await api.get(
          `/v1/tests/result/${this.resultId}/`,
          { headers: this.getAuthHeaders() }
        )

        const data = response.data?.result || response.data || {}

        this.result = {
          title:
            data.title ||
            data.ticket_name ||
            (data.ticket_size && data.ticket_number
              ? `${data.ticket_size} talik bilet #${data.ticket_number}`
              : this.$t('result.testResult')),

          totalQuestions: Number(data.total_questions || data.total || 0),
          correctCount: Number(data.correct_count || data.correct || 0),
          wrongCount: Number(data.wrong_count || data.wrong || 0),
          unansweredCount: Number(data.unanswered_count || data.unanswered || 0),
          percent: Number(data.score_percent || data.percent || 0),

          failedByMistakes: !!data.failed_by_mistakes,

          backRoute:
            data.back_route ||
            (data.ticket_size ? `/tickets/${data.ticket_size}` : '/')
        }
      } catch (error) {
        console.error('Natija yuklash xatosi:', error)
        this.errorMessage =
          error?.response?.data?.detail ||
          error?.response?.data?.message ||
          this.$t('result.fetchError')
      } finally {
        this.loading = false
      }
    },

    goHome() {
      this.$router.push('/')
    },

    restartTest() {
      if (this.result?.backRoute && this.result.backRoute !== '/') {
        this.$router.push(this.result.backRoute)
      } else {
        this.$router.push('/')
      }
    },

    goBackToCategory() {
      if (this.result?.backRoute?.startsWith('/tickets/')) {
        this.$router.push(this.result.backRoute)
      } else {
        this.$router.push('/')
      }
    }
  }
}
</script>

<style scoped>
* {
  box-sizing: border-box;
}

.page-wrapper {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  width: 100%;
  overflow-x: hidden;
}

.page-content {
  flex: 1;
  padding: 0 12px;
  width: 100%;
  max-width: 100%;
}

@media (min-width: 768px) {
  .page-content {
    padding: 0 16px;
  }
}

@media (min-width: 1200px) {
  .page-content {
    padding: 0 24px;
  }
}

/* Responsive container */
.result-wrap {
  width: 100%;
  max-width: 100%;
  margin: 0 auto;
}

@media (min-width: 768px) {
  .result-wrap {
    max-width: 90%;
  }
}

@media (min-width: 1024px) {
  .result-wrap {
    max-width: 1000px;
  }
}

.result-card {
  padding: 16px;
  width: 100%;
  overflow-x: hidden;
}

@media (min-width: 480px) {
  .result-card {
    padding: 20px;
  }
}

@media (min-width: 768px) {
  .result-card {
    padding: 24px;
  }
}

/* Result top section - fully responsive */
.result-top {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 20px;
  width: 100%;
}

@media (max-width: 480px) {
  .result-top {
    flex-direction: column;
    text-align: center;
    gap: 12px;
    margin-bottom: 16px;
  }
}

.result-icon {
  width: 56px;
  height: 56px;
  border-radius: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 28px;
  background: linear-gradient(135deg, rgba(59,130,246,0.18), rgba(29,78,216,0.24));
  border: 1px solid var(--border, #e5e7eb);
  flex-shrink: 0;
}

@media (min-width: 480px) {
  .result-icon {
    width: 64px;
    height: 64px;
    font-size: 30px;
    border-radius: 20px;
  }
}

@media (min-width: 768px) {
  .result-icon {
    width: 74px;
    height: 74px;
    font-size: 34px;
    border-radius: 22px;
  }
}

.result-title-wrapper {
  flex: 1;
  min-width: 0;
}

.result-title {
  margin: 0;
  font-size: 18px;
  color: var(--text, #1f2937);
  line-height: 1.3;
  font-weight: 600;
  word-break: break-word;
}

@media (min-width: 480px) {
  .result-title {
    font-size: 20px;
  }
}

@media (min-width: 640px) {
  .result-title {
    font-size: 24px;
  }
}

@media (min-width: 768px) {
  .result-title {
    font-size: 28px;
  }
}

@media (min-width: 1024px) {
  .result-title {
    font-size: 30px;
  }
}

.result-subtitle {
  margin: 6px 0 0;
  color: var(--text-soft, #6b7280);
  font-size: 12px;
  word-break: break-word;
  line-height: 1.4;
}

@media (min-width: 480px) {
  .result-subtitle {
    font-size: 13px;
    margin-top: 8px;
  }
}

@media (min-width: 768px) {
  .result-subtitle {
    font-size: 14px;
  }
}

@media (min-width: 1024px) {
  .result-subtitle {
    font-size: 15px;
  }
}

/* Stats grid - fully responsive with CSS Grid */
.stats-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
  margin: 16px 0;
  width: 100%;
}

@media (min-width: 480px) {
  .stats-grid {
    gap: 14px;
    margin: 18px 0;
  }
}

@media (min-width: 640px) {
  .stats-grid {
    gap: 16px;
    margin: 20px 0;
  }
}

@media (min-width: 768px) {
  .stats-grid {
    gap: 20px;
  }
}

@media (min-width: 1024px) {
  .stats-grid {
    grid-template-columns: repeat(4, 1fr);
    gap: 24px;
  }
}

.stat-box {
  border-radius: 14px;
  padding: 12px;
  border: 1px solid var(--border, #e5e7eb);
  background: rgba(255,255,255,0.03);
  text-align: center;
  width: 100%;
}

@media (min-width: 480px) {
  .stat-box {
    padding: 14px;
    border-radius: 16px;
  }
}

@media (min-width: 640px) {
  .stat-box {
    padding: 16px;
  }
}

@media (min-width: 768px) {
  .stat-box {
    padding: 18px;
    border-radius: 18px;
  }
}

.stat-label {
  color: var(--text-soft, #6b7280);
  font-size: 11px;
  margin-bottom: 6px;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

@media (min-width: 480px) {
  .stat-label {
    font-size: 12px;
    margin-bottom: 8px;
  }
}

@media (min-width: 640px) {
  .stat-label {
    font-size: 13px;
  }
}

@media (min-width: 768px) {
  .stat-label {
    font-size: 14px;
  }
}

.stat-value {
  font-size: 20px;
  font-weight: 800;
  color: var(--text, #1f2937);
  line-height: 1.2;
  word-break: break-word;
}

@media (min-width: 480px) {
  .stat-value {
    font-size: 22px;
  }
}

@media (min-width: 640px) {
  .stat-value {
    font-size: 26px;
  }
}

@media (min-width: 768px) {
  .stat-value {
    font-size: 30px;
  }
}

.success-box {
  background: rgba(34,197,94,0.10);
}

.danger-box {
  background: rgba(239,68,68,0.10);
}

.primary-box {
  background: rgba(59,130,246,0.10);
}

/* Progress section */
.result-progress {
  margin-top: 20px;
  width: 100%;
}

@media (min-width: 480px) {
  .result-progress {
    margin-top: 22px;
  }
}

@media (min-width: 768px) {
  .result-progress {
    margin-top: 24px;
  }
}

.result-progress-head {
  display: flex;
  justify-content: space-between;
  margin-bottom: 8px;
  color: var(--text, #1f2937);
  font-weight: 600;
  flex-wrap: wrap;
  gap: 8px;
  font-size: 13px;
}

@media (min-width: 480px) {
  .result-progress-head {
    font-size: 14px;
    margin-bottom: 10px;
  }
}

@media (min-width: 768px) {
  .result-progress-head {
    font-size: 15px;
    font-weight: 700;
  }
}

/* Info notes - fully responsive */
.info-note,
.warning-note {
  margin-top: 16px;
  padding: 12px;
  border-radius: 12px;
  line-height: 1.5;
  word-break: break-word;
  font-size: 12px;
}

@media (min-width: 480px) {
  .info-note,
  .warning-note {
    margin-top: 18px;
    padding: 14px;
    font-size: 13px;
    border-radius: 14px;
  }
}

@media (min-width: 768px) {
  .info-note,
  .warning-note {
    margin-top: 20px;
    padding: 14px 16px;
    font-size: 14px;
    border-radius: 16px;
  }
}

.info-note {
  border: 1px solid rgba(59, 130, 246, 0.35);
  background: rgba(59, 130, 246, 0.12);
  color: var(--text, #1f2937);
}

.warning-note {
  border: 1px solid rgba(245, 158, 11, 0.35);
  background: rgba(245, 158, 11, 0.12);
  color: #f59e0b;
}

/* Action buttons - fully responsive */
.result-actions {
  display: flex;
  gap: 10px;
  margin-top: 20px;
  flex-direction: column;
  width: 100%;
}

@media (min-width: 480px) {
  .result-actions {
    gap: 12px;
    margin-top: 22px;
    flex-direction: row;
  }
}

@media (min-width: 768px) {
  .result-actions {
    gap: 16px;
    margin-top: 24px;
  }
}

.result-actions :deep(.ant-btn) {
  height: auto;
  padding: 8px 16px;
  font-size: 14px;
  font-weight: 500;
  border-radius: 12px;
  width: 100%;
}

@media (min-width: 480px) {
  .result-actions :deep(.ant-btn) {
    width: auto;
    min-width: 140px;
    padding: 8px 20px;
  }
}

@media (min-width: 768px) {
  .result-actions :deep(.ant-btn) {
    padding: 10px 24px;
    font-size: 16px;
    border-radius: 14px;
  }
}

.result-actions :deep(.ant-btn-lg) {
  height: 42px;
}

@media (min-width: 768px) {
  .result-actions :deep(.ant-btn-lg) {
    height: 48px;
  }
}

/* Ant Design component overrides for mobile */
:deep(.ant-card) {
  border-radius: 16px;
  overflow: hidden;
}

:deep(.ant-card-body) {
  padding: 16px;
}

@media (min-width: 480px) {
  :deep(.ant-card-body) {
    padding: 20px;
  }
}

@media (min-width: 768px) {
  :deep(.ant-card-body) {
    padding: 24px;
  }
}

:deep(.ant-progress-inner) {
  height: 6px !important;
}

@media (min-width: 480px) {
  :deep(.ant-progress-inner) {
    height: 8px !important;
  }
}

:deep(.ant-progress-bg) {
  height: 6px !important;
}

@media (min-width: 480px) {
  :deep(.ant-progress-bg) {
    height: 8px !important;
  }
}

:deep(.ant-alert) {
  border-radius: 12px;
  font-size: 13px;
}

@media (min-width: 768px) {
  :deep(.ant-alert) {
    border-radius: 16px;
    font-size: 14px;
  }
}

:deep(.ant-empty) {
  margin: 40px 0;
}

:deep(.ant-empty-description) {
  font-size: 14px;
}

/* Ensure no horizontal scroll on mobile */
@media (max-width: 768px) {
  body, .page-wrapper, .page-content {
    overflow-x: hidden;
    max-width: 100vw;
  }
  
  :deep(*) {
    max-width: 100%;
  }
}
</style>